/**
 * PlacementPage — `/production/purchasing/place/:purchaseOrderNo` (round 19;
 * order to invoice doc 57 §4): placing one purchase order by hand, on a full
 * page. "Place PO-K20547 with PDQ · 1 of 4". A set held together is one
 * purchase order, so one page listing its lines.
 *
 * - "How to place with {supplier}": the supplier's placement instructions
 *   (its integration profile's manual order settings) and *Open {supplier} ↗*; "No
 *   placement instructions yet" when it has none.
 * - Per line: the product page (where the supplier's product is known and it
 *   has a product page address), and the values to copy, in the supplier's
 *   terms where the dictionary maps them.
 * - Record placed: each line's expected cost and VAT, prefilled and editable
 *   (doc 52 §8 Q4), and the supplier's order reference; posted once for the
 *   whole purchase order with the lines shown. A 409 ("this purchase order
 *   changed") reloads the page.
 * - In the queue (`?queue=1`, from *Place one at a time →*): [Skip] [Record
 *   placed & next →], walking To place with the skipped ones left out on the
 *   server; the run's counts live in the URL. From the purchase order page:
 *   [Back to PO] [Record placed].
 *
 * - *Copy file link*, per line, once its artwork is released (doc 65 §3.1):
 *   issues the supplier's links to the line's print-ready files, shows them
 *   once (one per version and part, under their white-label names) and copies
 *   them. They are held in this page's state only and gone on leaving it;
 *   copying again issues new ones and the earlier ones stop working. A line
 *   with no link says why ("Supplied by BrandHub", "Not released yet").
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Copy, ExternalLink } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { ApiError } from "@shared-core/data/api/errors";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PlacementLine,
  PlacementView,
} from "@shared-core/data/order-to-invoice-types";
import {
  formatShortDateOrDash,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import {
  orderStepFailure,
  type OrderStepFailure,
  type OrderStepField,
} from "@shared-core/domain/order-to-invoice-presentation";
import { paths } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import { FailureBanner } from "../app/components/domain/orders/OrderDialogs";
import { SupplierFileLink } from "../app/components/domain/purchasing/SupplierFileLink";

const short = formatShortDateOrDash;

function CopyRow({ label, value }: { label: string; value: string }) {
  const capabilities = usePlatformCapabilities();
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)_auto] items-center gap-2 border-t border-border py-1.5 first:border-t-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-sm" title={value}>
        {value}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 px-2"
        aria-label={`Copy ${label}: ${value}`}
        onClick={() =>
          void capabilities.copyToClipboard(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
        }
      >
        <Copy aria-hidden className="size-3.5" />
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

/** Pence as the pounds an input shows: "214.00"; empty for none. */
const toPounds = (pence: number | null) =>
  pence === null ? "" : (pence / 100).toFixed(2);

/** The pounds typed, as pence; null when blank, NaN when it is not an amount. */
function toPence(pounds: string): number | null {
  const text = pounds.trim().replace(/^£/, "");
  if (!text) return null;
  return /^\d+(\.\d{1,2})?$/.test(text) ? Math.round(Number(text) * 100) : NaN;
}

interface LineCosts {
  cost: string;
  vat: string;
}

function initialCosts(placement: PlacementView): LineCosts[] {
  return placement.lines.map((l) => ({
    cost: toPounds(l.expected_cost_pence),
    vat: toPounds(l.expected_vat_pence),
  }));
}

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "not_found" }
  | { kind: "loaded"; placement: PlacementView; remaining: number | null };

export function PlacementPage() {
  const { purchaseOrderNo: rawNo } = useParams<{ purchaseOrderNo: string }>();
  const purchaseOrderNo = rawNo ? decodeURIComponent(rawNo) : "";
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isQueue = params.get("queue") === "1";
  const done = Number(params.get("done")) || 0;
  const skippedParam = params.get("skipped") ?? "";
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [supplierRef, setSupplierRef] = useState("");
  const [costs, setCosts] = useState<LineCosts[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const skipped = skippedParam.split(",").filter(Boolean);

  const load = useCallback(async (): Promise<LoadState> => {
    const [placement, rest] = await Promise.all([
      adapter.getPlacement(purchaseOrderNo),
      isQueue
        ? adapter.listPurchasing({
            tab: "to_place",
            kind: "purchase_order",
            exclude: skippedParam.split(",").filter(Boolean),
            pageSize: 1,
          })
        : Promise.resolve(null),
    ]);
    if (!placement) return { kind: "not_found" };
    return { kind: "loaded", placement, remaining: rest?.total ?? null };
  }, [adapter, purchaseOrderNo, isQueue, skippedParam]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    setSupplierRef("");
    setFailure(null);
    load()
      .then((next) => {
        if (cancelled) return;
        setState(next);
        if (next.kind === "loaded") setCosts(initialCosts(next.placement));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: "failed",
          message:
            err instanceof Error
              ? err.message
              : "Could not load the purchase order to place.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  /** On to the next in the queue (the server leaves out the skipped and this one), or back to Purchasing. */
  const goNext = async (counts: { done: number; skipped: string[] }) => {
    const next = await adapter.listPurchasing({
      tab: "to_place",
      kind: "purchase_order",
      exclude: [...counts.skipped, purchaseOrderNo],
      pageSize: 1,
    });
    const first = next.items[0];
    if (first) {
      navigate(
        paths.placement({
          purchaseOrderNo: first.purchase_order_no,
          queue: counts,
        }),
      );
      return;
    }
    navigate(paths.purchasing(), {
      state: {
        notice: `Done: placed ${counts.done}${counts.skipped.length > 0 ? `, skipped ${counts.skipped.length}` : ""}.`,
      },
    });
  };

  const recordPlaced = async (placement: PlacementView) => {
    setFailure(null);
    const lines = placement.lines.map((line, i) => ({
      line,
      cost: toPence(costs[i]?.cost ?? ""),
      vat: toPence(costs[i]?.vat ?? ""),
    }));
    const local: OrderStepFailure["fieldErrors"] = {};
    lines.forEach(({ cost, vat }, i) => {
      if (cost === null || Number.isNaN(cost))
        local[`lines.${i}.confirmed_cost_pence`] =
          "Enter the cost the supplier confirmed, in pounds.";
      if (vat !== null && Number.isNaN(vat))
        local[`lines.${i}.confirmed_vat_pence`] = "Enter the VAT in pounds.";
    });
    if (Object.keys(local).length > 0) {
      setFailure({
        summary:
          Object.keys(local).length === 1
            ? "One field needs correcting."
            : `${Object.keys(local).length} fields need correcting.`,
        fieldErrors: local,
      });
      return;
    }
    setBusy(true);
    try {
      await adapter.recordPlaced(placement, {
        supplier_reference: supplierRef,
        // The card payment is recorded through the Record placed command (the website agent); this page places by hand.
        paid_by_card: false,
        lines: lines.map(({ line, cost, vat }) => ({
          purchase_order_line_id: line.purchase_order_line_id,
          confirmed_cost_pence: cost!,
          ...(vat !== null ? { confirmed_vat_pence: vat } : {}),
        })),
      });
      if (isQueue) {
        await goNext({ done: done + 1, skipped });
      } else {
        navigate(paths.purchaseOrderDetail({ purchaseOrderNo }));
      }
    } catch (err) {
      setFailure(orderStepFailure(err));
      // Its lines changed while it was open: show what it holds now.
      if (err instanceof ApiError && err.status === 409) {
        const reloaded = await load().catch(() => null);
        if (reloaded) {
          setState(reloaded);
          if (reloaded.kind === "loaded")
            setCosts(initialCosts(reloaded.placement));
        }
      }
    } finally {
      setBusy(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        Loading the purchase order to place…
      </div>
    );
  }
  if (state.kind === "failed") {
    return (
      <Alert variant="destructive" role="alert">
        <AlertDescription>
          Could not load the purchase order to place: {state.message}
        </AlertDescription>
      </Alert>
    );
  }
  if (state.kind === "not_found" || state.placement.is_gone) {
    return (
      <Card className="mx-auto max-w-2xl p-12 text-center">
        <h1 className="mb-2 text-lg">Nothing to place here</h1>
        <p className="text-muted-foreground">
          {state.kind === "not_found"
            ? `There is no purchase order ${purchaseOrderNo} that you can see.`
            : `${purchaseOrderNo} has been placed, moved or cancelled.`}{" "}
          <Link to={paths.purchasing()} className="underline">
            Back to Purchasing
          </Link>
        </p>
      </Card>
    );
  }

  const { placement, remaining } = state;
  const supplier = placement.supplier_name;
  const position = done + skipped.length + 1;
  const total = done + skipped.length + Math.max(1, remaining ?? 1);
  const canRecord = placement.record_placed_url !== null;
  const errorOf = (field: OrderStepField) => failure?.fieldErrors[field];
  return (
    <div className="mx-auto max-w-4xl space-y-4 pb-24">
      <div>
        <p className="text-sm text-muted-foreground">
          <Link to={paths.purchasing()} className="hover:underline">
            Purchasing
          </Link>{" "}
          › To place
        </p>
        <h1 className="text-2xl">
          Place{" "}
          <Link
            to={paths.purchaseOrderDetail({ purchaseOrderNo })}
            className="hover:underline"
          >
            {placement.purchase_order_no}
          </Link>{" "}
          with {supplier}
          {isQueue && (
            <span className="text-muted-foreground">
              {" "}
              · {position} of {total}
            </span>
          )}
        </h1>
        <p className="text-sm text-muted-foreground">
          {[
            placement.customer_name,
            placement.lines.map((l) => l.order_line).join(", "),
            `released ${short(placement.released_on)}`,
            placement.needed_by &&
              `needed by ${formatWeekdayDateOrDash(placement.needed_by)}`,
            placement.lines.length > 1 &&
              `${placement.lines.length} lines placed together`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      {placement.not_placed_reason && (
        <Alert role="status">
          <AlertDescription>{placement.not_placed_reason}</AlertDescription>
        </Alert>
      )}

      <Card className="space-y-5 p-5">
        <section aria-labelledby="how-to-place" className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="how-to-place" className="text-base font-semibold">
              How to place with {supplier}
            </h2>
            {placement.ordering_url && (
              <Button asChild size="sm" variant="outline">
                <a
                  href={placement.ordering_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open {supplier}
                  <ExternalLink aria-hidden className="size-3.5" />
                </a>
              </Button>
            )}
          </div>
          {placement.instructions.length > 0 ? (
            <ol className="list-decimal space-y-0.5 pl-5 text-sm text-muted-foreground">
              {placement.instructions.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-muted-foreground">
              {placement.no_instructions_sentence}
            </p>
          )}
        </section>

        {placement.lines.map((line) => (
          <PlacementLineSection
            key={line.purchase_order_line_id}
            line={line}
            supplier={supplier}
          />
        ))}

        <form
          className="space-y-3 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            void recordPlaced(placement);
          }}
        >
          <FailureBanner failure={failure} />
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
              What {supplier} confirmed
            </legend>
            {placement.lines.map((line, i) => {
              const costField = `lines.${i}.confirmed_cost_pence` as const;
              const vatField = `lines.${i}.confirmed_vat_pence` as const;
              const costId = `placement-cost-${i}`;
              const vatId = `placement-vat-${i}`;
              return (
                <div
                  key={line.purchase_order_line_id}
                  className="grid grid-cols-[minmax(0,1fr)_9rem_9rem] items-start gap-2"
                >
                  <span className="pt-6 text-sm">
                    {line.order_line} {line.name}
                  </span>
                  <div className="space-y-1">
                    <Label htmlFor={costId} className="text-xs">
                      Cost, ex VAT (£)
                    </Label>
                    <Input
                      id={costId}
                      inputMode="decimal"
                      value={costs[i]?.cost ?? ""}
                      onChange={(e) =>
                        setCosts((all) =>
                          all.map((c, j) =>
                            j === i ? { ...c, cost: e.target.value } : c,
                          ),
                        )
                      }
                      aria-invalid={errorOf(costField) ? true : undefined}
                      aria-describedby={
                        errorOf(costField) ? `${costId}-error` : undefined
                      }
                    />
                    {errorOf(costField) && (
                      <p
                        id={`${costId}-error`}
                        className="text-sm text-red-600"
                      >
                        {errorOf(costField)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={vatId} className="text-xs">
                      VAT (£)
                    </Label>
                    <Input
                      id={vatId}
                      inputMode="decimal"
                      value={costs[i]?.vat ?? ""}
                      onChange={(e) =>
                        setCosts((all) =>
                          all.map((c, j) =>
                            j === i ? { ...c, vat: e.target.value } : c,
                          ),
                        )
                      }
                      aria-invalid={errorOf(vatField) ? true : undefined}
                      aria-describedby={
                        errorOf(vatField) ? `${vatId}-error` : undefined
                      }
                    />
                    {errorOf(vatField) && (
                      <p id={`${vatId}-error`} className="text-sm text-red-600">
                        {errorOf(vatField)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </fieldset>
          <div className="space-y-1">
            <Label htmlFor="placement-supplier-ref">
              {supplier}&apos;s order reference
            </Label>
            <Input
              id="placement-supplier-ref"
              value={supplierRef}
              onChange={(e) => setSupplierRef(e.target.value)}
              aria-invalid={errorOf("supplier_reference") ? true : undefined}
              aria-describedby={
                errorOf("supplier_reference")
                  ? "placement-supplier-ref-error"
                  : undefined
              }
              className="w-60"
            />
            {errorOf("supplier_reference") && (
              <p
                id="placement-supplier-ref-error"
                className="text-sm text-red-600"
              >
                {errorOf("supplier_reference")}
              </p>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {isQueue ? (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  void goNext({ done, skipped: [...skipped, purchaseOrderNo] })
                }
              >
                Skip
              </Button>
            ) : (
              <Button asChild type="button" variant="outline">
                <Link to={paths.purchaseOrderDetail({ purchaseOrderNo })}>
                  Back to {placement.purchase_order_no}
                </Link>
              </Button>
            )}
            {canRecord && (
              <Button type="submit" variant="outline" disabled={busy}>
                {isQueue ? "Record placed & next →" : "Record placed"}
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}

function PlacementLineSection({
  line,
  supplier,
}: {
  line: PlacementLine;
  supplier: string;
}) {
  return (
    <section
      aria-label={`${line.order_line} ${line.name}`}
      className="space-y-1 border-t border-border pt-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-medium">
          {line.order_line} {line.name} ·{" "}
          {line.quantity.toLocaleString("en-GB")}
        </h3>
        {line.product_url && (
          <a
            href={line.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm underline-offset-2 hover:underline"
          >
            Product page · {line.product_code}
            <ExternalLink aria-hidden className="size-3" />
          </a>
        )}
      </div>
      <div>
        {line.values.map((v) => (
          <CopyRow key={v.label} label={v.label} value={v.value} />
        ))}
        <SupplierFileLink
          line={line}
          supplier={supplier}
          unavailableReason={line.file_link_unavailable_reason}
        />
      </div>
    </section>
  );
}
